import React, { useState, useRef, useEffect } from 'react';
import BasicDetails from './BasicDetails';
import AssignEmployee from './AssignEmployee';
import Summary from './Summary';
import ScheduleStatus from './ScheduleStatus';
import { createCrew, createCrewAssign, getCrewAll, getCrewAssignment, updateCrewAssignment,CrewScheduleWorkmen } from '../../services/crew';
import { Modal, message } from 'antd';
import dayjs from 'dayjs';

function isTimeRangeOverlap(start1, end1, start2, end2) {
  const toMinutes = t => {
    if (!t || typeof t !== 'string' || !/^\d{2}:\d{2}$/.test(t)) return NaN;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  const s1 = toMinutes(start1), e1 = toMinutes(end1);
  const s2 = toMinutes(start2), e2 = toMinutes(end2);
  if ([s1, e1, s2, e2].some(isNaN)) return false;
  return s1 < e2 && s2 < e1;
}

const MainStepper = () => {
  const [current, setCurrent] = useState(0);
  const [basicDetails, setBasicDetails] = useState({});
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [assignDate, setAssignDate] = useState(null);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [crewAssignments, setCrewAssignments] = useState([]);
  const [successToAssign, setSuccessToAssign] = useState([]);
  const [conflictToAssign, setConflictToAssign] = useState([]);
  const [crewId, setCrewId] = useState(''); // Store crewId in state for later use
  const basicDetailsFormRef = useRef();
  const assignEmployeeFormRef = useRef();

    // Step handlers


  const handleBasicDetailsNext = async () => {
    if (basicDetailsFormRef.current) {
      try {
        const values = await basicDetailsFormRef.current.validate();
        setBasicDetails(values);
        setCurrent(1);
      } catch (err) {
        // Validation error, do not proceed
      }
    }
  };

  const handleAssignEmployeeNext = (data) => {
    setAssignedEmployees(data.employees);
    setAssignDate(data.date);
    setCurrent(2);
  };

  const handleAssignEmployeePrev = () => setCurrent(0);
  const handleSummaryPrev = () => setCurrent(1);

  // Extracted actual save logic to a separate function
  const handleFinalSave = async (employeesToAssign) => {
    const payloadCreateCrew = { ...basicDetails, scheduleEmployee: employeesToAssign.length };
    

    

    try {
      // Save crew and get crewId
      const crewRes = await createCrew(payloadCreateCrew);
      let newCrewId =
        crewRes?.data?._id ||
        crewRes?.data?.crew?._id ||
        crewRes?.data?.crewId;

      if (!newCrewId) {
        try {
          const crewListRes = await getCrewAll();
          const crews = crewListRes.data?.data || [];
          let latestCrew = null;
          if (crews.length > 0) {
            latestCrew = crews
              .filter(c => c.crewName === basicDetails.crewName)
              .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];
            if (!latestCrew) latestCrew = crews[crews.length - 1];
          }
          newCrewId = latestCrew?._id;
        } catch (err) {}
      }

      if (!newCrewId) {
        message.error('Crew creation failed: No crew ID returned');
        return;
      }

      setCrewId(newCrewId); // Save crewId for later use

      // ✅ Call CrewScheduleWorkmen here if you want to schedule after crew creation
      // Pass the correct payload as per your backend API
      try {
        const payloadScheduleUkg = {
          payscale: basicDetails?.payscale,
          premium: basicDetails?.premium,
          event: basicDetails?.event,
          task: basicDetails?.task,
          startTime: basicDetails?.startTime,
          endTime: basicDetails?.endTime,
          employees: employeesToAssign // include employees in the payload
        };
        // Make sure CrewScheduleWorkmen is implemented to send payloadScheduleUkg as the POST body
        // Example implementation in services/crew.js:
        // return axios.post('/api/crew/scheduleWorkmen', payloadScheduleUkg);
        const scheduleRes = await CrewScheduleWorkmen(employeesToAssign);
        console.log("CrewScheduleWorkmen response:", scheduleRes);
      } catch (err) {
        console.error("CrewScheduleWorkmen error:", err);
      }

      const date =
        assignDate && dayjs.isDayjs(assignDate)
          ? assignDate.format('YYYY-MM-DD')
          : typeof assignDate === 'string'
          ? assignDate
          : '';

      // Fetch all assignments for conflict check BEFORE pushing new assignments
      const response = await getCrewAssignment();
      const assignments = response?.data?.data;
      setCrewAssignments(assignments);

      // Separate success/conflict employees BEFORE pushing to DB
      const successList = [];
      const conflictList = [];
      employeesToAssign.forEach((emp) => {
        const scheduleIn = emp.scheduleIn || '';
        const scheduleOut = emp.scheduleOut || '';
        const empId = emp.id || emp.employeeId || '';
        const conflict = (assignments || []).find(a =>
          a.employeeId === empId &&
          a.date === date &&
          isTimeRangeOverlap(scheduleIn, scheduleOut, a.start, a.end)
        );
        if (conflict) {
          conflictList.push(emp);
        } else {
          successList.push(emp);
        }
      });
      setSuccessToAssign(successList);
      setConflictToAssign(conflictList);

      setScheduleModalVisible(true);

      // Call handleSuccessAssignEmployee here with correct crewId and successList
      await handleSuccessAssignEmployee(successList, newCrewId, date);
    } catch (error) {
      message.error('Error creating crew or assigning employees');
    }
  };

  // Update handleSave to accept updated employees from Summary
  const handleSave = async (updatedEmployees) => {
    setAssignedEmployees(updatedEmployees);
    await handleFinalSave(updatedEmployees);
    // Remove handleSuccessAssignEmployee from here
  };

  // Save success employees to crewAssignment
  const handleSuccessAssignEmployee = async (successList, crewId, date) => {
    const payloadCrewAssign = (successList || []).map((emp) => ({
      crew: crewId, // Pass crewId as ObjectId string
      employee: emp._id,
      date,
      start: emp.scheduleIn || '',
      end: emp.scheduleOut || '',
      isScheduleOverridden: false,
      employeeId: emp.id,
    }));

    if (payloadCrewAssign.length > 0) {
      await createCrewAssign(payloadCrewAssign);
    }
  };

  // In ScheduleStatus, after override, push assignments to DB
  const handleOverrideSchedule = async () => {
    const date =
      assignDate && dayjs.isDayjs(assignDate)
        ? assignDate.format('YYYY-MM-DD')
        : typeof assignDate === 'string'
        ? assignDate
        : '';

    // 1. Update existing assignments for conflict employees to isScheduleOverridden: true
    for (const emp of conflictToAssign) {
      const existingAssignment = crewAssignments.find(a =>
        a.employeeId === emp.id &&
        a.date === date
      );
      if (existingAssignment) {
        await updateCrewAssignment(existingAssignment._id, { isScheduleOverridden: true });
      }
    }

    // 2. Add new assignments for conflict employees
    const payloadCrewAssign = (conflictToAssign || []).map((emp) => ({
      crew: crewAssignments.length > 0 ? crewAssignments[0].crew : '', // or use crewId if available
      employee: emp._id,
      date,
      start: emp.scheduleIn || '',
      end: emp.scheduleOut || '',
      isScheduleOverridden: false,
      employeeId: emp.id,
    }));

    if (payloadCrewAssign.length > 0) {
      await createCrewAssign(payloadCrewAssign);
    }

    // Reset all state to start over
    setCurrent(0);
    setBasicDetails({});
    setAssignedEmployees([]);
    setAssignDate(null);
    setScheduleModalVisible(false);
    setSuccessToAssign([]);
    setConflictToAssign([]);
  };


  const handleCancelSchedule = () => {
    setScheduleModalVisible(false);
    // Optionally keep data or reset as needed
  };

  return (
    <div>
      {/* Stepper UI */}
      <div className="crew-stepper">
        {['Basic Details', 'Assign Employees', 'Summary'].map((title, idx) => (
          <React.Fragment key={title}>
            <div className={`crew-stepper-item${current === idx ? ' active' : ''}`}>
              <span className="crew-stepper-circle">{idx + 1}</span>
              <span className="crew-stepper-label">{title}</span>
            </div>
            {idx < 2 && <div className="crew-stepper-line"></div>}
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 32 }}>
        {current === 0 && (
          <BasicDetails
            ref={basicDetailsFormRef}
            initialValues={basicDetails}
            onChange={setBasicDetails}
            onNext={handleBasicDetailsNext}
          />
        )}
        {current === 1 && (
          <AssignEmployee
            ref={assignEmployeeFormRef}
            basicDetails={basicDetails}
            initialEmployees={assignedEmployees}
            onPrev={handleAssignEmployeePrev}
            onNext={handleAssignEmployeeNext}
          />
        )}
        {current === 2 && (
          <Summary
            basicDetails={basicDetails}
            employees={assignedEmployees}
            assignDate={assignDate}
            onPrev={handleSummaryPrev}
            // Pass handleSave that receives updated employees from Summary
            onSave={(updatedEmployees) => handleSave(updatedEmployees)}
          />
        )}
      </div>
      <Modal
        open={scheduleModalVisible}
        footer={null}
        closable={false}
        width={950}
        bodyStyle={{ padding: 0, borderRadius: 12, overflow: 'hidden' }}
      >
        <ScheduleStatus
          assignedEmployees={assignedEmployees}
          crewAssignments={crewAssignments}
          assignDate={
            assignDate && dayjs.isDayjs(assignDate)
              ? assignDate.format('YYYY-MM-DD')
              : typeof assignDate === 'string'
              ? assignDate
              : ''
          }
          onOverride={handleOverrideSchedule}
          onCancel={handleCancelSchedule}
          successToAssign={successToAssign}
          conflictToAssign={conflictToAssign}
        />
      </Modal>
      <style>{`
        .crew-stepper {
          display: flex;
          align-items: center;
          margin-bottom: 24px;
        }
        .crew-stepper-item {
          display: flex;
          align-items: center;
          color: #888;
          font-size: 14px;
        }
        .crew-stepper-item.active {
          color: #1677ff;
          font-weight: 500;
        }
        .crew-stepper-circle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #f0f0f0;
          color: #888;
          font-size: 13px;
          margin-right: 6px;
        }
        .crew-stepper-item.active .crew-stepper-circle {
          background: #1677ff;
          color: #fff;
        }
        .crew-stepper-label {
          margin-right: 8px;
        }
        .crew-stepper-line {
          flex: 1;
          height: 2px;
          background: #eee;
          margin: 0 8px;
        }
      `}</style>
    </div>
  );
};







export default MainStepper;





